/** biome-ignore-all lint/a11y/useSemanticElements: disclosure header uses button for toggle */
import { Icon } from "./Icon";
import "./Disclosure.css";
import chevronRightIcon from "@ms/chevron_right.svg?raw";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import type { TextColor } from "@ui/typography/Text";
import {
	type Accessor,
	type Component,
	createContext,
	createMemo,
	createSignal,
	createUniqueId,
	type JSX,
	type ParentComponent,
	splitProps,
	useContext,
} from "solid-js";

export type DisclosureApi = {
	isOpen: Accessor<boolean>;
	toggle: () => void;
	/** Set the panel to open. Safe to call when already open. */
	open: () => void;
	/** Id of the collapsible panel; set `aria-controls` on header toggles. */
	panelId: string;
	/** Id of the default heading toggle when `headerIsButton` is true. */
	headingTriggerId: string;
	/** Chevron toggle wired to this disclosure (use inside {@link DisclosureProps.renderHeader}). */
	Toggle: Component<DisclosureToggleProps>;
};

type DisclosureContextValue = DisclosureApi;

const DisclosureContext = createContext<DisclosureContextValue>();

type DisclosureToggleProps = {
	class?: string;
	/** Accessible label when the toggle is icon-only. */
	ariaLabel?: string;
};

export type DisclosureProps = {
	defaultOpen?: boolean;
	/** Called after the panel opens or closes (toggle or {@link DisclosureApi.open}). */
	onOpenChange?: (isOpen: boolean) => void;
	/**
	 * Custom header with access to open state and toggle.
	 * Use when the header includes interactive controls (e.g. delete) beside the chevron.
	 */
	renderHeader?: (api: DisclosureApi) => JSX.Element;
	/** First child: heading. Second child: panel. Ignored when `renderHeader` is set. */
	children: JSX.Element | [JSX.Element, JSX.Element];
	variant?: "default" | "white";
	/** When true (default), the whole heading is one toggle button. */
	headerIsButton?: boolean;
} & JSX.HTMLAttributes<HTMLDivElement>;

function createDisclosureApi(
	isOpen: Accessor<boolean>,
	toggle: () => void,
	open: () => void,
	panelId: string,
	headingTriggerId: string,
): DisclosureApi {
	const api: DisclosureApi = {
		isOpen,
		toggle,
		open,
		panelId,
		headingTriggerId,
		Toggle: (toggleProps) => (
			<DisclosureToggle disclosure={api} {...toggleProps} />
		),
	};
	return api;
}

/**
 * Expandable/collapsible content section with heading and panel.
 *
 * [Design System →](http://localhost:5174/component/Disclosure)
 */
export const Disclosure: ParentComponent<DisclosureProps> = (props) => {
	const [local, rest] = splitProps(props, [
		"children",
		"defaultOpen",
		"onOpenChange",
		"variant",
		"headerIsButton",
		"renderHeader",
		"class",
		"style",
	]);

	const uid = createUniqueId();
	const panelId = `gvtui-disclosure-panel-${uid}`;
	const headingTriggerId = `gvtui-disclosure-trigger-${uid}`;

	const [isOpen, setIsOpen] = createSignal(local.defaultOpen ?? false);
	const headerIsButton = () => local.headerIsButton ?? true;
	const notifyOpenChange = (next: boolean) => local.onOpenChange?.(next);
	const toggle = () =>
		setIsOpen((open) => {
			const next = !open;
			notifyOpenChange(next);
			return next;
		});
	const open = () => {
		setIsOpen((current) => {
			if (!current) {
				notifyOpenChange(true);
			}
			return true;
		});
	};
	const api = createMemo(() =>
		createDisclosureApi(isOpen, toggle, open, panelId, headingTriggerId),
	);

	const headingChild = () => {
		const c = local.children;
		if (Array.isArray(c)) {
			return c[0];
		}
		return undefined;
	};

	const panelChild = () => {
		const c = local.children;
		if (local.renderHeader) {
			return c;
		}
		if (Array.isArray(c)) {
			return c[1];
		}
		return undefined;
	};

	const panelLabelledBy = () => {
		if (local.renderHeader || headerIsButton()) {
			return headingTriggerId;
		}
		return undefined;
	};

	return (
		<DisclosureContext.Provider value={api()}>
			<div
				class={`gvtui-disclosure gvtui-disclosure--${local.variant ?? "default"} ${local.class ?? ""}`.trim()}
				style={local.style}
				{...rest}
			>
				{local.renderHeader ? (
					<div class="gvtui-disclosure-heading gvtui-disclosure-heading--custom">
						{local.renderHeader(api())}
					</div>
				) : headerIsButton() ? (
					<DisclosureHeadingButton
						id={headingTriggerId}
						panelId={panelId}
						onClick={toggle}
						isOpen={isOpen}
					>
						{headingChild()}
					</DisclosureHeadingButton>
				) : (
					<div class="gvtui-disclosure-heading gvtui-disclosure-heading--custom">
						{headingChild()}
					</div>
				)}
				<div
					id={panelId}
					role="region"
					aria-labelledby={panelLabelledBy()}
					aria-hidden={!isOpen()}
					classList={{ open: isOpen() }}
					class="gvtui-disclosure-panel"
				>
					<div class="gvtui-disclosure-panel__content">{panelChild()}</div>
				</div>
			</div>
		</DisclosureContext.Provider>
	);
};

type DisclosureTogglePublicProps = DisclosureToggleProps & {
	/** Pass from {@link DisclosureProps.renderHeader} `api` when the toggle is not a direct child of `Disclosure`. */
	disclosure?: DisclosureApi;
};

/**
 * Chevron toggle for {@link Disclosure}.
 */
export const DisclosureToggle = (props: DisclosureTogglePublicProps) => {
	const [local] = splitProps(props, ["disclosure", "class", "ariaLabel"]);
	const contextApi = useContext(DisclosureContext);
	const disclosure = () => local.disclosure ?? contextApi;
	const isOpen = () => disclosure()?.isOpen() ?? false;
	const toggleTriggerId = createUniqueId();
	const triggerId = () =>
		`gvtui-disclosure-toggle-${toggleTriggerId.replace(/:/g, "")}`;

	return (
		<button
			type="button"
			id={triggerId()}
			class={`gvtui-disclosure-toggle ${local.class ?? ""}`}
			onClick={() => disclosure()?.toggle()}
			aria-expanded={isOpen()}
			aria-controls={disclosure()?.panelId}
			aria-label={local.ariaLabel ?? "Toggle section"}
			disabled={!disclosure()}
		>
			<Icon
				icon={chevronRightIcon}
				display="inline-flex"
				class="gvtui-disclosure-icon"
			/>
		</button>
	);
};

const DisclosureHeadingButton: ParentComponent<{
	id: string;
	panelId: string;
	onClick: () => void;
	isOpen: Accessor<boolean>;
}> = (props) => {
	return (
		<button
			type="button"
			id={props.id}
			class="gvtui-disclosure-heading"
			onClick={props.onClick}
			aria-expanded={props.isOpen()}
			aria-controls={props.panelId}
		>
			{props.children}
			<DisclosureHeadingIcon color="fg4" />
		</button>
	);
};

export const DisclosureHeadingIcon = (props: { color?: TextColor }) => {
	return (
		<MaterialSymbol
			symbol="chevron_right"
			class="gvtui-disclosure-icon"
			color={props.color ?? "fg4"}
			relativeSize={1.7}
		/>
	);
};
